import { useState, useRef, useEffect } from 'react';
import Button, { BUTTON_KIND } from '@/components/Button';
import Modal, { DialogTitleClassName } from '@/components/Modals/Modal';
import { PlusIcon } from '@heroicons/react/24/outline';
import { detectUserCountry } from '@/utils/countryDetection';
import { getRedirectUrl } from '@/utils/shopifyRedirect';
import QuantityButton from '@/components/QuantityButton';
import useShopify from '@/hooks/useShopify';
import useAuth from '@/hooks/useAuth';
import { CheckoutProductType } from '@/types/IAnalytics';
import analyticsAPI from '@/api/analytics';
import { ErrorAlert } from '@/components/Alert';
import MESSAGES from '@/constants/messages-en';

interface ImagePosition {
    right?: string;
    top?: string;
    transform?: string;
    width?: string;
    defaultScale?: number;
    hoverScale?: number;
    selectedScale?: number;
}

interface ItemProps {
    title: string;
    subtitle: string;
    image: string;
    selected: boolean;
    onClick: () => void;
    imagePosition?: ImagePosition;
}

export default function AddMoreProfilesModal() {
    const prevIsOpenRef = useRef<boolean>(false);
    const { taptCustom, taptDigitalProfile, proceedToCheckout } = useShopify();
    const { orgID } = useAuth();
    const shopRef = useRef<HTMLAnchorElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selected, setSelected] = useState<CheckoutProductType | null>(null);
    const [quantity, setQuantity] = useState(1);
    const [error, setError] = useState<string | null>(null);

    // Record checkout click when modal is opened
    useEffect(() => {
        if (isOpen && !prevIsOpenRef.current && orgID) {
            analyticsAPI.recordCheckoutClick(orgID);
        }
        prevIsOpenRef.current = isOpen;
    }, [isOpen, orgID]);


    const handleAddProfiles = () => {
        if (!selected) return;
        setIsLoading(true);

        const product = selected === 'custom_card' ? taptCustom : taptDigitalProfile;
        if (!product) {
            setIsLoading(false);
            return;
        }

        analyticsAPI.recordCheckoutStart(orgID!, {
            product_type: selected,
            quantity,

        }).then(() => {
            setError(null);
            proceedToCheckout(product.variants[0].id, quantity, [
                {
                    key: '_orgId',
                    value: String(orgID),
                }
            ]);
        }).catch(() => {
            setError(MESSAGES.addMoreProfiles.error);
            setIsLoading(false);
        });
    };

    const goToShop = () => {
        // Check if we already have a URL in the anchor element
        if (
            shopRef.current?.href &&
            !shopRef.current.href.endsWith('/placeholder')
        ) {
            shopRef.current.click();
            return;
        }

        // Generate market-specific redirect URL based on detected country
        try {
            const countryCode = detectUserCountry();
            const redirectUrl = getRedirectUrl(countryCode);

            // Set the URL on the anchor element and click it
            if (shopRef.current) {
                shopRef.current.href = redirectUrl;
                shopRef.current.click();
            }
        } catch (error) {
            console.error(
                'Failed to generate Shopify redirect URL:',
                error,
            );
            // Fallback to default URL
            const fallbackUrl =
                'https://tapt.io/collections/all-products';

            if (shopRef.current) {
                shopRef.current.href = fallbackUrl;
                shopRef.current.click();
            }
        }
    };

    // Local Item component for card selection
    const Item = ({
        title,
        subtitle,
        image,
        selected,
        onClick,
        imagePosition = {
            right: '0',
            top: '50%',
            transform: 'translateY(-50%)',
            defaultScale: 1,
            hoverScale: 1.1,
            selectedScale: 1.25
        }
    }: ItemProps) => {
        const [isHovered, setIsHovered] = useState(false);

        return (
            <div
                onClick={onClick}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className={`relative cursor-pointer transition-all h-36 bg-white shadow-sm border ${selected ? 'border-brand-500' : 'border-gray-200 hover:border-gray-300'} group overflow-hidden rounded-tl-3xl rounded-br-3xl rounded-tr-lg rounded-bl-lg`}
                style={{ minWidth: 0 }}
            >
                {/* Background gradient - white on left fading to transparent on right */}
                <div
                    className={`absolute inset-0 bg-gradient-to-r from-white via-white to-transparent z-10 transition-all duration-300 ease-in-out ${selected || isHovered ? 'w-2/3' : 'w-full'}`}
                />

                {/* Image container - positioned on the right side */}
                <div
                    className={`absolute top-0 right-0 bottom-0 w-1/2 z-0 transition-all duration-500 origin-right ${selected ? 'opacity-100' : isHovered ? 'opacity-90' : 'opacity-50'}`}
                    style={{
                        transform: `scale(${selected ? imagePosition.selectedScale : isHovered ? imagePosition.hoverScale : imagePosition.defaultScale})`,
                        transitionTimingFunction: 'cubic-bezier(0.8, 0, 0.16, 1)',
                    }}
                >
                    <img
                        src={image}
                        alt={title}
                        className="h-full w-auto max-w-none object-contain object-right-center"
                        style={{ position: 'absolute', right: imagePosition.right, top: imagePosition.top, transform: imagePosition.transform }}
                        draggable={false}
                    />
                </div>

                {/* Text content - positioned at bottom left */}
                <div className="absolute bottom-0 left-0 p-2 z-20">
                    <div className="text-xs text-gray-500 mb-1">{subtitle}</div>
                    <div className="text-xl font-medium text-gray-900 leading-tight">{title}</div>
                </div>
            </div>
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            buttonTitle={MESSAGES.addMoreProfiles.buttonTitle}
            buttonKind={BUTTON_KIND.PRIMARY}
            icon={<PlusIcon className="w-5 h-5" />}
            successButtonText={MESSAGES.addMoreProfiles.goToCheckout}
            onSuccess={() => handleAddProfiles()}
            isDisabled={quantity === 0 || selected === null}
            cancelButton={<QuantityButton initialValue={1} onChange={setQuantity} />}
            isLoading={isLoading}
        >
            <div className="flex gap-4 mb-4">
                <h3 className={DialogTitleClassName}>{MESSAGES.addMoreProfiles.modalTitle}</h3>
                <div className="flex-1 flex justify-end">
                    <a ref={shopRef} href="placeholder" target="_blank" rel="noopener noreferrer" className="hidden" />
                    <Button
                        kind={BUTTON_KIND.PRIMARY}
                        onClick={goToShop}
                        buttonText={MESSAGES.addMoreProfiles.shopAllProducts}
                    />
                </div>
            </div>

            {error && (
                <ErrorAlert
                    message={error}
                />
            )}

            <div className="flex flex-col gap-4 mt-4">
                <Item
                    title={MESSAGES.addMoreProfiles.customCards.title}
                    subtitle={MESSAGES.addMoreProfiles.customCards.subtitle}
                    image="https://assets.tapt.io/add-more-custom-cards.png"
                    selected={selected === 'custom_card'}
                    onClick={() => setSelected('custom_card')}
                    imagePosition={{
                        right: '-15%',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        defaultScale: 1.5,
                        hoverScale: 1.7,
                        selectedScale: 1.7
                    }}
                />
                <Item
                    title={MESSAGES.addMoreProfiles.digitalProfiles.title}
                    subtitle={MESSAGES.addMoreProfiles.digitalProfiles.subtitle}
                    image="https://assets.tapt.io/add-more-digital-profiles.png"
                    selected={selected === 'digital_profile'}
                    onClick={() => setSelected('digital_profile')}
                    imagePosition={{
                        right: '-15%',
                        top: '80%',
                        transform: 'translateY(-50%)',
                        defaultScale: 1.5,
                        hoverScale: 1.7,
                        selectedScale: 1.7
                    }}
                />
            </div>
        </Modal>
    );
}